import { GripVertical, Plus, Sparkles, Zap, Edit2, Trash2, Utensils } from 'lucide-react';
import { useState } from 'react';
import type { Equipment } from '../../../../../../common/src/types';
import { INITIAL_FORM, DEFAULT_S_CONF_EQUIPMENTS } from '../../../../constants/equipment.constants';
import { EquipmentFormData } from '../../../../types/equipment.types';
import { Button } from '../../../ui/button';
import { Card } from '../../../ui/card';
import { EquipmentForm } from '../EquipmentForm';

interface Step8EquipmentProps {
  data: any;
  onChange: (updates: any) => void;
  onSave?: () => void;
  isSaving?: boolean;
}

export function Step8Equipment({ data, onChange }: Step8EquipmentProps) {
  const equipments = data.equipments || [];
  const equipmentsEnabled = !!data.equipmentsEnabled;
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<EquipmentFormData>(INITIAL_FORM);

  const onEquipmentsChange = (newEquipments: Equipment[]) => {
    onChange({ equipments: newEquipments, equipmentsEnabled });
  };

  const handleToggleEnabled = (enabled: boolean) => {
    onChange({ equipmentsEnabled: enabled, equipments });
  };

  const handleAdd = () => {
    if (!formData.name.trim()) return;
    const newEquipment: Equipment = {
      id: `temp-${Date.now()}`,
      spaceTypeId: '',
      name: formData.name,
      description: formData.description || undefined,
      price: formData.price,
      unit: formData.unit,
      iconName: formData.iconName || undefined,
      minCapacity: formData.minCapacity || undefined,
      maxCapacity: formData.maxCapacity || undefined,
      triggersRestaurant: formData.triggersRestaurant,
      order: equipments.length,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    onEquipmentsChange([...equipments, newEquipment]);
    setFormData(INITIAL_FORM);
    setIsAdding(false);
  };

  const handleEdit = (equipment: Equipment) => {
    setEditingId(equipment.id);
    setFormData({
      name: equipment.name,
      description: equipment.description || '',
      price: equipment.price,
      unit: equipment.unit,
      iconName: equipment.iconName || '',
      minCapacity: equipment.minCapacity || null,
      maxCapacity: equipment.maxCapacity || null,
      triggersRestaurant: equipment.triggersRestaurant,
    });
  };

  const handleSaveEdit = () => {
    if (!editingId) return;
    const updated = equipments.map((eq: Equipment) =>
      eq.id === editingId
        ? {
            ...eq,
            name: formData.name,
            description: formData.description || undefined,
            price: formData.price,
            unit: formData.unit,
            iconName: formData.iconName || undefined,
            minCapacity: formData.minCapacity || undefined,
            maxCapacity: formData.maxCapacity || undefined,
            triggersRestaurant: formData.triggersRestaurant,
            updatedAt: new Date(),
          }
        : eq,
    );
    onEquipmentsChange(updated);
    setEditingId(null);
    setFormData(INITIAL_FORM);
  };

  const handleReorder = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= equipments.length) return;
    const reordered = [...equipments];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    onEquipmentsChange(reordered.map((eq, idx) => ({ ...eq, order: idx })));
  };

  const handleLoadDefaults = () => {
    if (equipments.length > 0 && !confirm('Cela remplacera vos équipements actuels. Continuer ?')) return;
    const defaults: Equipment[] = DEFAULT_S_CONF_EQUIPMENTS.map((eq, idx) => ({
      ...eq,
      id: `temp-default-${idx}-${Date.now()}`,
      spaceTypeId: '',
      order: idx,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    }));
    onChange({ equipmentsEnabled: true, equipments: defaults });
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-[rgba(0,117,141,0.05)] border-[rgba(0,117,141,0.2)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${equipmentsEnabled ? 'bg-[#00758d] text-white' : 'bg-gray-200 text-gray-500'}`}><Zap size={24} /></div>
            <div>
              <h4 className="font-bold text-[#00303c]">Activer le module d'options</h4>
              <p className="text-sm text-[#005a6d]">Permet de proposer des équipements et services payants</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" checked={equipmentsEnabled} onChange={(e) => handleToggleEnabled(e.target.checked)} />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00758d]"></div>
          </label>
        </div>
      </Card>

      {equipmentsEnabled && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2 text-[#00303c]">Équipements & Options payantes</h3>
              <p className="text-sm text-gray-600">Configurez les équipements et options disponibles.</p>
            </div>
            <Button type="button" onClick={handleLoadDefaults} variant="outline" size="sm" className="text-[#00758d] border-[#00758d]"><Sparkles className="w-4 h-4 mr-2" /> Charger les défauts</Button>
          </div>

          <div className="space-y-3">
            {equipments.map((equipment: Equipment, index: number) => (
              <Card key={equipment.id} className="p-4 border-[rgba(0,117,141,0.1)]">
                {editingId === equipment.id ? (
                  <EquipmentForm formData={formData} onChange={setFormData} onSave={handleSaveEdit} onCancel={() => setEditingId(null)} />
                ) : (
                  <div className="flex items-start gap-3">
                    <button type="button" onClick={() => handleReorder(index, 'up')} disabled={index === 0} className="text-gray-400 hover:text-[#00758d] disabled:opacity-30 mt-2"><GripVertical className="w-4 h-4" /></button>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-bold text-[#00303c]">{equipment.name}</h4>
                          {equipment.description && <p className="text-sm text-gray-500 mt-1 italic">{equipment.description}</p>}
                          <div className="flex items-center gap-4 mt-3 text-xs bg-gray-50 p-2 rounded-lg">
                            <span className="font-bold text-[#00758d] px-2 py-1 bg-white rounded border border-[rgba(0,117,141,0.1)]">
                              {equipment.price === 0 ? (equipment.unit?.toLowerCase().includes('devis') ? 'Sur devis' : 'Inclus') : `${equipment.price.toLocaleString()} ${equipment.unit}`}
                            </span>
                            {equipment.minCapacity && <span className="flex items-center gap-1 text-gray-500 font-medium"><Plus size={12} /> {equipment.minCapacity} pers.</span>}
                            {equipment.triggersRestaurant && <span className="text-[#dfc32f] font-bold flex items-center gap-1"><Utensils size={14} /> Déclenche restauration</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleEdit(equipment)} className="hover:bg-[#00758d]/5"><Edit2 className="w-4 h-4 text-[#00758d]" /></Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => { if (confirm('Supprimer ?')) onEquipmentsChange(equipments.filter((e: any) => e.id !== equipment.id)) }} className="hover:bg-red-50"><Trash2 className="w-4 h-4 text-[#8E0B56]" /></Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>

          {isAdding ? (
            <Card className="p-4 border-[rgba(0,117,141,0.2)] shadow-md">
              <EquipmentForm formData={formData} onChange={setFormData} onSave={handleAdd} onCancel={() => setIsAdding(false)} />
            </Card>
          ) : (
            <Button type="button" onClick={() => setIsAdding(true)} variant="outline" className="w-full border-dashed border-2 py-6"><Plus className="w-4 h-4 mr-2" /> Ajouter une option personnalisée</Button>
          )}
        </>
      )}
    </div>
  );
}
